import { z } from "zod";

import type { TimeWindow, TimeZoneMode } from "@/shared/components/Timeline";
import {
  filterSchema,
  type Filter,
  type FilterMatchMode,
} from "@/shared/domain/query/filters";

/** The user's filters on the tab's sessions, and how they combine. */
export interface OverviewFilters {
  filters: Filter[];
  matchMode: FilterMatchMode;
}

export const NO_FILTERS: OverviewFilters = { filters: [], matchMode: "AND" };

/**
 * The Overview tab's query string. Keys the tab does not own are left alone.
 * - `session` — the selected session's id.
 * - `from`, `to` — the window's edges as integer epoch milliseconds; zone-free,
 *   so a link opens on the same span in any browser.
 * - `tz=utc` — read every time on the UTC clock; absent means local.
 * - `filters` — `JSON.stringify({ filters, matchMode })`, the shape the
 *   Metrics dashboard writes to its own `?filters=`. Only the shape is shared:
 *   Metrics names fields with an entity prefix (`session.name`) the sessions
 *   query does not use, so its links do not carry over.
 */
export interface OverviewUrl {
  session: string | null;
  timeWindow: TimeWindow | null;
  tz: TimeZoneMode;
  filters: OverviewFilters;
}

const SESSION = "session";
const FROM = "from";
const TO = "to";
const TZ = "tz";
/** Exported so a caller can parse and memoize the filters from this one value's text. */
export const FILTERS_PARAM = "filters";

/** `{ filters, matchMode }`, as the Metrics dashboard writes it: a missing match mode is AND. */
const filtersSchema = z.object({
  filters: z.array(filterSchema),
  matchMode: z.enum(["AND", "OR"]).default("AND"),
});

/**
 * The filters a `?filters=` value holds. Missing, malformed or anything that
 * is not filter state reads as no filters, so a truncated link opens the
 * device's sessions unfiltered rather than an error.
 */
export function readFilters(text: string | null): OverviewFilters {
  if (!text) {
    return NO_FILTERS;
  }
  try {
    const parsed = filtersSchema.safeParse(JSON.parse(text));
    return parsed.success ? parsed.data : NO_FILTERS;
  } catch {
    return NO_FILTERS;
  }
}

/** `text` as a non-negative safe integer; `null` when missing or anything else (`-5`, `1.5`, `1e3`). */
function readMs(text: string | null): number | null {
  if (text === null || !/^\d+$/.test(text)) {
    return null;
  }
  const value = Number(text);
  return Number.isSafeInteger(value) ? value : null;
}

/**
 * What the query string says, except the filters (read them with
 * `readFilters`, so a caller can key them on the `filters` value's text). A
 * window needs both edges, in order; anything less reads as no window, so a
 * hand-edited link falls back to the default rather than to an empty span.
 * An empty `session=` is no selection, and any `tz` other than `utc` is local.
 */
export function parseOverviewUrl(
  params: URLSearchParams,
): Omit<OverviewUrl, "filters"> {
  const from = readMs(params.get(FROM));
  const to = readMs(params.get(TO));
  return {
    session: params.get(SESSION) || null,
    tz: params.get(TZ) === "utc" ? "utc" : "local",
    timeWindow:
      from !== null && to !== null && from < to
        ? { start: from, end: to }
        : null,
  };
}

/**
 * `params` with the given keys rewritten: a key in `patch` set to its default
 * (`null`, local time, no filters matching all) is removed, a key not in
 * `patch` is kept as it was. Returns a new object.
 */
export function writeOverviewUrl(
  params: URLSearchParams,
  patch: Partial<OverviewUrl>,
): URLSearchParams {
  const next = new URLSearchParams(params);
  if ("session" in patch) {
    const session = patch.session ?? null;
    if (session === null) {
      next.delete(SESSION);
    } else {
      next.set(SESSION, session);
    }
  }
  if ("timeWindow" in patch) {
    const timeWindow = patch.timeWindow ?? null;
    if (timeWindow === null) {
      next.delete(FROM);
      next.delete(TO);
    } else {
      // Zooming leaves fractional milliseconds; a link does not need them.
      next.set(FROM, String(Math.round(timeWindow.start)));
      next.set(TO, String(Math.round(timeWindow.end)));
    }
  }
  if (patch.tz !== undefined) {
    // Local is the default, so it is written as no key at all.
    if (patch.tz === "utc") {
      next.set(TZ, "utc");
    } else {
      next.delete(TZ);
    }
  }
  if (patch.filters !== undefined) {
    // No filters, matching all, is the default, so it is written as no key
    // at all. Matching any is kept even with no filters, so it survives
    // removing the last filter before adding the next one.
    if (
      patch.filters.filters.length === 0 &&
      patch.filters.matchMode === "AND"
    ) {
      next.delete(FILTERS_PARAM);
    } else {
      next.set(
        FILTERS_PARAM,
        JSON.stringify({
          filters: patch.filters.filters,
          matchMode: patch.filters.matchMode,
        }),
      );
    }
  }
  return next;
}
